'use client'

import dynamic from 'next/dynamic'
import { alpha } from '@mui/material/styles'
import { Alert, IconButton } from '@mui/material'
import { closeSnackbar } from 'notistack'

// Types
import type { AlertItemProps } from '@/types/components/atoms/item/alert'

// Components
const CloseIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/close'))
const DangerIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/danger'))
const ErrorIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/error'))
const InfoIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/info'))
const TickIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/tick'))

const AlertItemAtom = (props: AlertItemProps) => {
	// Props
	const { ref, id, variant, severity, children } = props

	// Variables
	const alphaValue = variant === 'outlined' ? 0.1 : variant === 'filled' ? 0.7 : variant === 'standard' ? 0.05 : 0.0

	return (
		<Alert
			ref={ref}
			variant={variant === 'transparent' ? 'standard' : (variant ?? 'filled')}
			severity={severity}
			iconMapping={{
				error: <ErrorIconAtom fontSize="inherit" />,
				success: <TickIconAtom fontSize="inherit" />,
				warning: <DangerIconAtom fontSize="inherit" />,
				info: <InfoIconAtom fontSize="inherit" />
			}}
			action={
				id && (
					<IconButton
						size="small"
						color="inherit"
						aria-label="close"
						onClick={() => closeSnackbar(id)}
						sx={{
							borderRadius: 1,
							'& .MuiTouchRipple-root .MuiTouchRipple-child': { borderRadius: 1 }
						}}
					>
						<CloseIconAtom fontSize="small" />
					</IconButton>
				)
			}
			sx={theme => ({
				userSelect: 'none',
				bgcolor:
					severity === 'success'
						? alpha(theme.palette.success.main, alphaValue)
						: severity === 'info'
							? 'primary.ultralight'
							: severity === 'warning'
								? alpha(theme.palette.warning.main, alphaValue)
								: alpha(theme.palette.error.main, alphaValue),
				backdropFilter: 'blur(5px)',
				'& .MuiAlert-icon': { color: severity === 'info' ? 'primary.dark' : 'inherit' }
			})}
		>
			{children}
		</Alert>
	)
}

export default AlertItemAtom
